import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { platformFetch } from "@/services/http/platform-fetch";

/** `onCreated` lets the console refresh its school list (S13b) and open the new school (S14). */
export function CreateTenantForm({
  onCreated,
}: { onCreated?: (created: { tenantId: string; slug: string }) => void } = {}) {
  const t = useTranslations("platform");
  const tRoot = useTranslations();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    const res = await platformFetch("/platform/admin/tenants", {
      method: "POST",
      body: JSON.stringify({ slug, name, adminEmail, adminName }),
    });

    setLoading(false);

    if (!res.ok) {
      setError(
        apiErrorText(await readApiErrorResponse(res), tRoot, "platform.createFailed", {
          namespace: "platform.errors",
        }),
      );
      return;
    }

    const json = await res.json();
    setMessage(t("createSuccess", { slug: json.data.slug as string }));
    setSlug("");
    setName("");
    setAdminEmail("");
    setAdminName("");
    setConfirmed(false);
    onCreated?.({ tenantId: json.data.tenantId as string, slug: json.data.slug as string });
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="create-tenant-form">
      <h2 className="text-lg font-medium">{t("createTenant")}</h2>
      <div>
        <label htmlFor="create-tenant-tenant-slug" className="mb-1 block text-sm">
          {t("slugLabel")}
        </label>
        <input
          id="create-tenant-tenant-slug"
          data-testid="tenant-slug"
          required
          aria-describedby="create-tenant-tenant-slug-hint"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
        />
        <p id="create-tenant-tenant-slug-hint" className="mt-1 text-xs text-[var(--text-muted)]">
          {t("slugHint")}
        </p>
      </div>
      <div>
        <label htmlFor="create-tenant-tenant-name" className="mb-1 block text-sm">
          {t("displayNameLabel")}
        </label>
        <input
          id="create-tenant-tenant-name"
          data-testid="tenant-name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
        />
      </div>
      <div>
        <label htmlFor="create-tenant-admin-email" className="mb-1 block text-sm">
          {t("adminEmailLabel")}
        </label>
        <input
          id="create-tenant-admin-email"
          data-testid="admin-email"
          type="email"
          required
          value={adminEmail}
          onChange={(e) => setAdminEmail(e.target.value)}
          className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
        />
      </div>
      <div>
        <label htmlFor="create-tenant-admin-name" className="mb-1 block text-sm">
          {t("adminNameLabel")}
        </label>
        <input
          id="create-tenant-admin-name"
          data-testid="admin-name"
          required
          value={adminName}
          onChange={(e) => setAdminName(e.target.value)}
          className="w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-[var(--text-primary)]"
        />
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="confirm-slug-permanent"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="mt-1"
        />
        <span>{t("confirmSlugPermanent", { slug: slug || "…" })}</span>
      </label>
      {error && (
        <p className="text-sm text-red-600" data-testid="create-tenant-error">
          {error}
        </p>
      )}
      {message && (
        <p
          className="text-sm text-green-700 dark:text-green-400"
          data-testid="create-tenant-success"
        >
          {message}
        </p>
      )}
      <button
        type="submit"
        data-testid="create-tenant-submit"
        disabled={loading || !confirmed}
        className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-50"
      >
        {loading ? t("creating") : t("createTenant")}
      </button>
    </form>
  );
}
